import { Table } from "core/table/Table";
import "./Modal.scss";
import Icon from "core/icon/Icon";
import Button from "core/buttons/button/Button";
import { FormattedMessage } from "react-intl";
import { MONTHS, objAndRealizedTypes, POPUP_STATUS } from "constants/constants";
import { FORM_INFO } from "constants/forms";
import { Typography } from "core/typography/Typography";
import moment from "moment";
import deleteCircleIcon from "assets/icons/delete-circle.svg";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputSearchList from "core/inputs/input-search/InputSearchList";
import { useEffect, useState } from "react";
import { useObjectivesContext } from "api/datatrack/objectives/ObjectivesProvider";
import propTypes from "prop-types";
import {
  getUserName,
  getYearsDropdown,
  headerItems,
  validateTables,
  onChange,
  onAdd,
  getTypes,
  onDelete,
  onChangeTable,
} from "utils/objectivesHelper";
import { useUserContext } from "api/user/UserProvider";
import InputText from "core/inputs/input-text/InputText";
import { formatAllUsers } from "components/form/CommonInputs";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import AlertPopup from "components/alert-popup/AlertPopup";

const ObjectivesAndRealizedModal = ({
  closeModal,
  lang,
  refresh,
  setRefresh,
}) => {
  const {
    objectives,
    createNewUserObjectives,
    getObjectives,
    fetchTeamMembers,
    teamMembers,
    updateUserObjectives,
    deleteUserObjectives,
  } = useObjectivesContext();
  const [updatedObjectives, setUpdatedObjectives] = useState([]);
  const { getStaffById, staffById } = useStaffContext();
  const { connectedUser, userScope } = useUserContext();
  const [newObjectives, setNewObjectives] = useState([]);
  const currentYear = moment().year();
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [objectivesArray, setObjectivesArray] = useState([...objectives]);
  const [elementsToDelete, setElementsToDelete] = useState([]);

  const objectivesSubtableHeader = MONTHS.map((month) => ({
    label: {
      key: month,
      value: (
        <FormattedMessage
          id={`${month.slice(0, 3)}`}
          defaultMessage={`${month.slice(0, 3)}`}
        />
      ),
    },
    id: month,
    value: month,
  }));

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Lock the background page scroll while the modal is open so the dashboard
  // filter bar cannot scroll into view over the modal. The page scrolls on the
  // root element (not the body), so overflow is locked on documentElement; the
  // modal keeps its own internal scroll (separate overflow container).
  useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  useEffect(() => {
    getStaffById(connectedUser.id);
    getObjectives({
      userScope: userScope,
      lang: lang,
    });
    setRefresh(false);
  }, [refresh, lang]);

  useEffect(() => {
    setObjectivesArray(objectives);
  }, [objectives]);

  const header = headerItems.map((item) => item);

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
  };

  const objectivesTable = (data, objectiveId) => {
    const currentObjIsReforcasted = objectives?.some(
      (obj) =>
        data.objectives?.type === 0 &&
        obj?.objectives?.type === 1 &&
        obj?.objectives?.year === data?.objectives?.year &&
        obj?.objectives?.concerned_user === data?.objectives?.concerned_user
    );
    return (
      <Table
        className="objectives-subtable"
        header={objectivesSubtableHeader}
        content={[
          objectivesSubtableHeader.map(({ id }) => (
            <div key={id}>
              <InputText
                value={data?.objectives?.[id] || ""}
                type="number"
                onChange={(e) => {
                  onChangeTable({
                    id: objectiveId || data.id,
                    key: id,
                    val: Number(e.target.value),
                    setData: setObjectivesArray,
                    data: objectivesArray,
                    setUpdatedObjectives,
                    setNewObjectives,
                    initialObjectives: objectives,
                  });
                }}
                readOnly={currentObjIsReforcasted}
              />
            </div>
          )),
        ]}
      />
    );
  };

  const getContent = () => {
    return objectivesArray.length > 0
      ? objectivesArray.map((item) => {
          const isNewRow = item.newRow;
          return {
            type: (
              <InputSelect
                id="type"
                value={item?.objectives?.type}
                options={getTypes({
                  objectives,
                  objAndRealizedTypes,
                })}
                name="type"
                setValue={(name, value) => {
                  onChange({
                    setUpdatedObjectives: setUpdatedObjectives,
                    updatedObjectives: updatedObjectives,
                    id: item.objectives.id || item.id,
                    key: name,
                    setData: setObjectivesArray,
                    data: objectivesArray,
                    val: value,
                    setNewObjectives: setNewObjectives,
                    newObjectives: newObjectives,
                    initialObjectives: objectives,
                  });
                }}
                readOnly={!isNewRow}
              />
            ),
            concerned_user: (() => {
              const userOptions = formatAllUsers(teamMembers);
              const currentValue =
                item?.person?.id || item?.objectives?.concerned_user;
              return (
                <InputSearchList
                  id="recipient"
                  limitChoice={1}
                  minLength={0}
                  options={userOptions}
                  popup={FORM_INFO.objective_recipient.popup}
                  selectedOptions={userOptions.filter(
                    (user) => user.value === currentValue
                  )}
                  setValue={(val) => {
                    return onChange({
                      setUpdatedObjectives: setUpdatedObjectives,
                      updatedObjectives: updatedObjectives,
                      id: item.objectives.id || item.id,
                      key: "concerned_user",
                      setData: setObjectivesArray,
                      data: objectivesArray,
                      val,
                      setNewObjectives,
                      initialObjectives: objectives,
                      newObjectives: newObjectives,
                    });
                  }}
                  readOnly={!isNewRow}
                />
              );
            })(),
            year: (
              <InputSelect
                id={`year`}
                options={getYearsDropdown(currentYear)?.map((year) => ({
                  value: year?.value,
                  label: year?.label,
                }))}
                name="year"
                value={item?.objectives?.year}
                setValue={(name, value) => {
                  onChange({
                    setUpdatedObjectives: setUpdatedObjectives,
                    updatedObjectives: updatedObjectives,
                    id: item.objectives.id || item.id,
                    key: "year",
                    setData: setObjectivesArray,
                    data: objectivesArray,
                    val: value || currentYear,
                    setNewObjectives,
                    initialObjectives: objectives,
                  });
                }}
                readOnly={!isNewRow}
              />
            ),
            objectives: objectivesTable(item, item?.objectives?.id),
            update_by: getUserName(staffById),
            date: moment(item.updated_at).format("DD/MM/YYYY"),
            delete: (
              <button
                className="delete-icon-button"
                onClick={() => {
                  onDelete({
                    id: item.objectives?.id || item?.id,
                    data: objectivesArray,
                    setData: setObjectivesArray,
                    getUpdatedValues: setUpdatedObjectives,
                    setElementsToDelete,
                    newObjectives,
                    setNewObjectives,
                  });
                }}
              >
                <img
                  src={deleteCircleIcon}
                  alt="delete"
                  className="delete-icon"
                />
              </button>
            ),
          };
        })
      : [];
  };

  return (
    <>
      <div className="overlay-style objectives-and-realized-overlay" />
      <div className="modal-style objectives-and-realized-modal">
        <div className="close-modal-btn">
          <Icon color="gray-200" icon="close" onClick={closeModal} />
        </div>
        <div className="modal_body header_th">
          <AlertPopup
            code={code}
            setCode={setCode}
            setStatus={setStatus}
            status={status}
          />
          <Table
            id="objectives-and-realized-modal-table"
            header={header}
            content={getContent()}
            rowSubHeader={objectivesSubtableHeader}
          />
          <button
            className="button-obj"
            onClick={() =>
              onAdd({
                header,
                data: objectivesArray,
                setData: setObjectivesArray,
                defaultYear: currentYear,
              })
            }
          >
            <Typography
              className="add-new-objectives link-3"
              type="div"
              label={
                <FormattedMessage
                  id="add_objectives"
                  defaultMessage="Nouveaux objectifs"
                />
              }
            />
          </button>
        </div>
        <div className="objectives-and-realized-modal-buttons-wrapper">
          <Button
            className="btn-standard btn-outline"
            type="button"
            onClick={() => {
              setRefresh(true);
              closeModal();
            }}
            id="cancel"
          >
            <FormattedMessage id="cancel" defaultMessage="Annuler" />
          </Button>
          <Button
            color="btn-medium btn-standard gray-700"
            onClick={() => {
              return validateTables({
                initialObjectives: objectives,
                newObjectives: newObjectives,
                objectivesArray: objectivesArray,
                setObjectivesArray,
                setNewObjectives,
                setElementsToDelete,
                setRefresh,
                createObjectives: createNewUserObjectives,
                editObjectives: updateUserObjectives,
                deleteObjectives: deleteUserObjectives,
                setError,
                elementsToDelete,
                closeModal: closeModal,
              });
            }}
            id="validate"
          >
            <FormattedMessage id="validate" defaultMessage="Valider" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default ObjectivesAndRealizedModal;

ObjectivesAndRealizedModal.propTypes = {
  closeModal: propTypes.func,
  lang: propTypes.string,
  refresh: propTypes.bool,
  setRefresh: propTypes.func,
};
